"use client";
import { useSearchParams } from "next/navigation";
import ImageUI from "./CustomImage/ImageUI";
import Link from "next/link";
import { cn } from "@pulsex/utils";
import { useSearch, useSearchSections } from "@pulsex/hooks";
import pulseConfig from "@/pulsex.config";
import Spinner from "./Spinner";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import FilterDropdown from "./shared/FilterDropdown";

export default function SearchResults() {
    const params = useSearchParams();
    const searchValue = params?.get("q")?.replaceAll("+", " ");
    const [orderBy, setOrderBy] = useState<0 | 1>(0);
    const [sections, setSections] = useState<string>("");
    const searchQ = useSearch(pulseConfig, {
        q: searchValue,
        orderBy,
        types: sections ? sections.split(",").filter(s => !!s) : [],
        postsPerPage: 10,
    });

    const filtersQ = useSearchSections({
        config: pulseConfig,
    });

    return (
        <main className="min-h-screen">
            <section className="container mx-auto my-5">
                <div className="flex justify-between items-center">
                    <h1 className="text-blue font-bold text-3xl dark:text-white lg:text-lg">
                        Search Results &apos;{searchValue}&apos;
                    </h1>
                    <div className="flex items-center justify-end gap-4 lg:hidden">
                        <button
                            className={cn(
                                "whitespace-nowrap py-1.5 px-8 text-blue dark:text-white font-semibold border border-blue dark:border-gray-500 rounded-lg transition-all",
                                orderBy === 0 &&
                                    "border-primary bg-primary dark:border-primary dark:bg-primary text-white"
                            )}
                            onClick={() => setOrderBy(0)}
                        >
                            By Date
                        </button>
                        <button
                            className={cn(
                                "whitespace-nowrap py-1.5 px-8 text-blue dark:text-white font-semibold border border-blue dark:border-gray-500 rounded-lg transition-all",
                                orderBy === 1 &&
                                    "border-primary bg-primary dark:border-primary dark:bg-primary text-white"
                            )}
                            onClick={() => setOrderBy(1)}
                        >
                            By Relevance
                        </button>
                        {filtersQ.data ? (
                            <FilterDropdown
                                options={Object.entries(filtersQ.data).map(([key, value]) => ({
                                    label: key,
                                    value: value,
                                }))}
                                className="text-blue h-[38px] px-4 py-1 bg-[#f2f2f2] rounded-lg w-48 cursor-pointer dark:border dark:border-gray-500 dark:bg-darkbg dark:text-white md:w-full"
                                name="Filter"
                                onChange={(value:any) => {
                                    setSections(value);
                                }}
                                loading={filtersQ.isLoading}

                            />
                        ) : (
                            ""
                        )}
                    </div>
                </div>
                <div className="my-8 lg:my-4">
                    {searchQ?.isLoading ? (
                        <div className="flex justify-center my-40">
                            <Spinner />
                        </div>
                    ) : (
                        searchQ?.data?.posts?.map((post: any) => (
                            <div
                                className="grid grid-cols-12 gap-3 mb-4 lg:mb-9"
                                key={post.id}
                            >
                                <Link
                                    href={post?.url}
                                    className="col-span-4 lg:col-span-12"
                                >
                                    <ImageUI data={post} cropSize="rectangle" resolution={3}/>
                                </Link>
                                <div className="col-span-8 lg:col-span-12">
                                    {post?.tag?.title && (
                                        <p
                                            className={cn(
                                                "bg-primary rounded py-1 px-6 text-xs font-medium text-white block w-fit mb-2",
                                                "lg:text-[10px] lg:p-1 lg:px-2 lg:mb-1"
                                            )}
                                        >
                                            {post.tag.title}
                                        </p>
                                    )}
                                    <Link href={post?.url}>
                                        <h2 className="text-lg font-bold mb-2 dark:text-white lg:text-base">
                                            {post?.title}
                                        </h2>
                                        <p className="text-base opacity-70 mb-4 dark:text-white line-clamp-6 xl:line-clamp-3 lg:line-clamp-none">
                                            {post?.summary}
                                        </p>
                                        <p className="text-xs opacity-50 dark:text-white lg:text-xs">
                                            {post?.displayDate}
                                        </p>
                                    </Link>
                                </div>
                            </div>
                        ))
                    )}
                </div>
                {searchQ.hasNextPage && (
                    <div className="flex justify-center items-center my-20 lg:my-10">
                        <button
                            onClick={searchQ.fetchNextData as any}
                            className={cn(
                                "px-10 py-2 rounded border border-blue text-blue dark:border-gray-500 dark:text-white",
                                searchQ.isFetchingNextPage && "opacity-50"
                            )}
                            disabled={searchQ.isFetchingNextPage}
                        >
                            Show More
                        </button>
                    </div>
                )}
                {!searchQ?.isLoading && searchQ?.data?.posts?.length == 0 && (
                    <div className="text-gray-400 text-xl mt-5 mb-72">No data found</div>
                )}
            </section>
        </main>
    );
}
